import React, { useState, useEffect, useRef } from 'react';

export const fmtWk = (totalSec) => {
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
};

const beep = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = 880; osc.type = 'sine';
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.start(); osc.stop(ctx.currentTime + 0.55);
  } catch (e) { /* silêncio se o browser bloquear áudio */ }
};

// Execução de treino compartilhada entre a aba Academia e o card da tela
// principal. Clique na atividade: se não é a ativa, só troca (fica verde
// rodando). Se já é a ativa e está rodando, conclui a série e entra em
// descanso (fica amarela). Se está descansando, clicar pula o descanso.
// Ao zerar o descanso sozinho, volta a ficar verde (toca beep + vibra).
function WorkoutExecutor({ svc, routineId, routineName, exercises, onFinish, onCancel }) {
  const [exec, setExec] = useState(null);
  const [rest, setRest] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const restFiredRef = useRef(false);
  const wakeLockRef = useRef(null);

  const requestWakeLock = async () => {
    try { if ('wakeLock' in navigator) wakeLockRef.current = await navigator.wakeLock.request('screen'); }
    catch (e) { /* navegador sem suporte ou negou: segue sem travar a tela */ }
  };
  const releaseWakeLock = async () => {
    try { await wakeLockRef.current?.release(); } catch (e) { /* ignora */ }
    wakeLockRef.current = null;
  };

  // Começa a contar assim que o componente monta — quem decide "iniciar" é o
  // pai, só renderizando o executor depois do clique em "Iniciar treino".
  useEffect(() => {
    const t = Date.now();
    setExec({
      start: t, list: exercises,
      activeIdx: 0,
      accum: exercises.map(() => 0),
      restAccum: exercises.map(() => 0),
      activeStart: t,
      setsDone: exercises.map(() => 0),
      restsDone: exercises.map(() => 0),
    });
    setNow(t);
    requestWakeLock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!exec && !rest) return undefined;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [exec, rest]);

  // Fecha o segmento de descanso: soma o tempo parado (desde que a pausa
  // começou, não desde que o exercício ficou ativo) no restAccum. O relógio
  // total do exercício (activeStart) não é tocado aqui — ele nunca pausa,
  // conta contínuo desde o clique até trocar de exercício ou finalizar.
  const endRestSegment = () => {
    if (!rest) return;
    const t = Date.now();
    const startedAt = rest.startedAt;
    setExec((e) => {
      if (!e) return e;
      const restAccum = [...e.restAccum];
      restAccum[e.activeIdx] += (t - startedAt) / 1000;
      return { ...e, restAccum };
    });
  };

  // Beep + vibração quando o descanso zera, e volta pro verde sozinho.
  useEffect(() => {
    if (!rest) { restFiredRef.current = false; return undefined; }
    const remaining = (rest.endsAt - now) / 1000;
    if (remaining <= 0 && !restFiredRef.current) {
      restFiredRef.current = true;
      beep();
      if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
      const id = setTimeout(() => { endRestSegment(); setRest(null); }, 1200);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [rest, now]);

  // O sistema solta o wake lock quando o app vai pro fundo; repede ao voltar.
  useEffect(() => {
    if (!exec) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) requestWakeLock();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [exec]);

  useEffect(() => () => { releaseWakeLock(); }, []);

  // Fecha o relógio total do exercício que está deixando de ser ativo (conta
  // contínuo desde activeStart, pausa ou não) e passa pro outro índice. Se
  // tinha pausa rolando, fecha ela primeiro (relógio próprio, independente).
  const goToExercise = (idx) => {
    const t = Date.now();
    if (rest) { endRestSegment(); setRest(null); }
    setExec((e) => {
      if (!e || idx < 0 || idx >= e.list.length || idx === e.activeIdx) return e;
      const accum = [...e.accum];
      accum[e.activeIdx] += (t - e.activeStart) / 1000;
      return { ...e, accum, activeIdx: idx, activeStart: t };
    });
  };

  const startRest = (duration, idx) => {
    restFiredRef.current = false;
    setExec((e) => {
      if (!e) return e;
      const restsDone = [...e.restsDone];
      restsDone[idx] += 1;
      return { ...e, restsDone };
    });
    const t = Date.now();
    setRest({ startedAt: t, endsAt: t + duration * 1000, duration });
    setNow(t);
  };

  const completeSet = () => {
    if (!exec) return;
    setExec((e) => {
      const setsDone = [...e.setsDone];
      setsDone[e.activeIdx] += 1;
      return { ...e, setsDone };
    });
    const dur = Number(exec.list[exec.activeIdx]?.rest) || 60;
    startRest(dur, exec.activeIdx);
  };

  // Pausa sem série: mesmo descanso (conta como pausa), mas não incrementa
  // séries concluídas.
  const extraRest = () => {
    if (!exec) return;
    const dur = Number(exec.list[exec.activeIdx]?.rest) || 60;
    startRest(dur, exec.activeIdx);
  };

  const clickExercise = (idx) => {
    if (!exec) return;
    if (idx !== exec.activeIdx) { goToExercise(idx); return; }
    if (rest) { endRestSegment(); setRest(null); return; }
    completeSet();
  };

  const finishWorkout = async () => {
    if (!exec) return;
    const t = Date.now();
    const accum = [...exec.accum];
    const restAccum = [...exec.restAccum];
    // Pausa em aberto fecha pelo próprio relógio dela (rest.startedAt), não
    // pelo activeStart — os dois relógios são independentes.
    if (rest) restAccum[exec.activeIdx] += (t - rest.startedAt) / 1000;
    // Relógio total do exercício ativo sempre fecha aqui: ele nunca pausou,
    // contou contínuo (trabalho + descanso) desde activeStart.
    accum[exec.activeIdx] += (t - exec.activeStart) / 1000;

    const outExercises = exec.list.map((ex, i) => ({
      name: ex.name,
      seconds: Math.round(accum[i] - restAccum[i]), // líquido: total menos pausa
      total_seconds: Math.round(accum[i]),
      rest_seconds: Math.round(restAccum[i]),
      carga: ex.carga, series: ex.series, reps: ex.reps,
      sets_done: exec.setsDone[i],
      rests_done: exec.restsDone[i] || 0,
    }));
    const totalSeconds = Math.round((t - exec.start) / 1000);
    const totalRestSeconds = Math.round(restAccum.reduce((a, b) => a + b, 0));
    setRest(null);
    releaseWakeLock();
    setSaving(true); setError(null);
    try {
      const d = await svc.saveWorkoutSession({
        routine_id: routineId, routine_name: routineName,
        started_at: new Date(exec.start).toISOString(),
        ended_at: new Date(t).toISOString(),
        total_seconds: totalSeconds,
        exercises: outExercises,
      });
      if (d.error) throw new Error(d.error);
      setExec(null);
      if (onFinish) {
        onFinish({
          exercises: outExercises,
          totalSeconds,
          totalRestSeconds,
          netSeconds: totalSeconds - totalRestSeconds,
        });
      }
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  const cancelWorkout = () => { setExec(null); releaseWakeLock(); if (onCancel) onCancel(); };

  if (!exec) return null;

  const totalElapsed = (now - exec.start) / 1000;
  const activeSec = exec.accum[exec.activeIdx] + (now - exec.activeStart) / 1000;
  const restRemaining = rest ? Math.ceil((rest.endsAt - now) / 1000) : 0;

  // Pausa acumulada até agora (todas as já fechadas + a que estiver rolando
  // neste instante) — e o líquido (trabalho puro) é só a subtração. Mostrado
  // ao vivo, igual ao resumo pós-treino (Tempo total / Pausas / Líquido).
  const pauseSoFar = exec.restAccum.reduce((a, b) => a + b, 0) + (rest ? (now - rest.startedAt) / 1000 : 0);
  const netSoFar = totalElapsed - pauseSoFar;

  return (
    <div className="wk-exec">
      {error && <p className="wk-msg wk-err">{error}</p>}
      <div className="wk-total">
        <span className="wk-total-label">{routineName ? `${routineName} · ` : ''}Tempo total</span>
        <span className="wk-total-time">{fmtWk(totalElapsed)}</span>
      </div>
      <div className="wk-total wk-total-sub">
        <span className="wk-total-label">Pausas</span>
        <span className="wk-total-time wk-total-time-sm">{fmtWk(pauseSoFar)}</span>
      </div>
      <div className="wk-total wk-total-sub">
        <span className="wk-total-label">Tempo de treino</span>
        <span className="wk-total-time wk-total-time-sm">{fmtWk(netSoFar)}</span>
      </div>

      {exec.list.map((ex, i) => {
        const isActive = i === exec.activeIdx;
        const resting = isActive && !!rest;
        const secs = isActive ? activeSec : exec.accum[i];
        const estado = isActive ? (resting ? 'resting' : 'running') : 'idle';
        const totalSeries = ex.series || '—';
        return (
          <div key={i} className={`wk-exec-ex wk-exec-${estado}`} onClick={() => clickExercise(i)}>
            <div className="wk-exec-ex-top">
              <span className="wk-exec-ex-name">{ex.name}</span>
              <span className="wk-exec-ex-time">
                {resting ? (restRemaining > 0 ? fmtWk(restRemaining) : 'Bora!') : fmtWk(secs)}
              </span>
            </div>
            {resting && <span className="wk-exec-ex-resting-label">Descanso — clique pra pular</span>}
            <div className="wk-exec-ex-stats">
              <div className="wk-exec-stat">
                <span className="wk-exec-stat-label">Peso</span>
                <span className="wk-exec-stat-value">{ex.carga || '—'}</span>
              </div>
              <div className="wk-exec-stat">
                <span className="wk-exec-stat-label">Repetições</span>
                <span className="wk-exec-stat-value">{ex.reps || '—'}</span>
              </div>
              <div className="wk-exec-stat">
                <span className="wk-exec-stat-label">Série</span>
                <span className="wk-exec-stat-value">{exec.setsDone[i]}/{totalSeries}</span>
              </div>
            </div>
          </div>
        );
      })}

      <div className="wk-exec-controls">
        <button className="wk-btn wk-btn-rest" onClick={extraRest}>⏱ Pausa sem série</button>
        <button className="wk-btn wk-btn-finish" onClick={finishWorkout} disabled={saving}>
          {saving ? 'Salvando…' : '🏁 Finalizar treino'}
        </button>
        <button className="wk-btn wk-btn-cancel" onClick={cancelWorkout}>Cancelar</button>
      </div>
    </div>
  );
}

export default WorkoutExecutor;
